import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { MaxBrainAudience } from 'app/library/audience/models/audience.model';
import { AudienceExperienceStatusEnum } from 'app/library/audience/enums/audience-experience-status.enum';
import { AudienceApiService, EXCLUDE_AUTOMATIC_AUDIENCE_LIST_OPTIONS } from 'app/library/audience/services/audience.api-service';
import { AudienceService } from 'app/library/audience/services/audience.service';
import { GrowNewSubAudienceDialogComponent } from 'app/library/grow/dialogs/grow-new-sub-audience-dialog/grow-new-sub-audience-dialog.component';
import { isAutomaticAudience, getRemoveAudienceDialogTexts } from 'app/library/grow/utils/audience-display.util';
import { ExperienceService } from 'app/library/experience/services/experience.service';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { MoveToDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/move-to-dialog/move-to-dialog.component';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { filter, takeUntil } from 'rxjs/operators';
import { SidenavService } from '../../../services/sidenav.service';
import { UserState } from 'app/projects/user/src/lib/models/user.state';

@Component({
    selector: 'app-audience-start',
    templateUrl: './audience-start.component.html',
    styleUrls: ['./audience-start.component.scss'],
})
export class AudienceStartComponent extends UnsubscribeOnDestroy implements OnInit {
    @HostListener('window:resize', ['$event'])
    onResize(_event?: UIEvent) {
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

        if (width > 1080) {
            this.isLargeScreen = true;
        } else {
            this.isLargeScreen = false;
        }
    }

    engageNewSubAudienceDialogRef: MatDialogRef<GrowNewSubAudienceDialogComponent>;
    moveToDialogRef: MatDialogRef<MoveToDialogComponent>;
    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    isLargeScreen = false;

    categories = [
        {
            id: 1,
            name: 'ENGAGE.AUDIENCE.LABELS.WIDGET_CONTENT',
            description: 'ENGAGE.AUDIENCE.LABELS.WIDGET_CONTENT.DESCRIPTION',
            lastUpdate: null,
            url: 'widgets',
            action: 'GENERAL.BUTTON.MANAGE',
            cls: 'widgets',
            img: null,
        },
        {
            id: 2,
            name: 'ENGAGE.AUDIENCE.LABELS.MEMBERS',
            description: 'ENGAGE.AUDIENCE.LABELS.MEMBERS.DESCRIPTION',
            memberCount: 0,
            url: 'users',
            action: 'GENERAL.BUTTON.EDIT',
            cls: 'members',
            img: null,
        },
        {
            id: 3,
            name: 'ENGAGE.AUDIENCE.LABELS.RELEVANT_EXPERIENCES',
            description: 'ENGAGE.AUDIENCE.LABELS.RELEVANT_EXPERIENCES.DESCRIPTION',
            experiencesCount: 0,
            url: 'relevant-experiences',
            action: 'GENERAL.BUTTON.MANAGE',
            cls: 'experiences',
            img: null,
        },
    ];

    audience: MaxBrainAudience;
    audienceId: string;
    totalTenantExperiencesCount = 0;
    isAutomaticAudienceFlag = false;

    constructor(
        private _audienceService: AudienceService,
        private _experienceService: ExperienceService,
        private _sidenavService: SidenavService,
        private _matDialog: MatDialog,
        private _router: Router,
        public audienceApiService: AudienceApiService,
        private _translateService: TranslateService,
        private _route: ActivatedRoute,
        private _store: Store,
        private _cd: ChangeDetectorRef
    ) {
        super();
        this.onResize();
    }

    private get _permissions(): string[] {
        return this._store.selectSnapshot(UserState.getMyInfo)?.permissions ?? [];
    }

    get hasUsersReadPermission(): boolean {
        return (
            this._permissions.includes(AdminPermissions.USERS_READ) || this._permissions.includes(AdminPermissions.TENANT_MANAGEMENT)
        );
    }

    isCategoryDisabled(category: { url: string }): boolean {
        return category.url === 'users' && !this.hasUsersReadPermission;
    }

    private async loadAudience(audienceId: string): Promise<MaxBrainAudience | null> {
        // Always prefer a fresh read so counters (e.g., memberCount) are up to date
        // after edits done in child routes and returning via back navigation.
        try {
            const audience = await this.audienceApiService.read(audienceId).toPromise();
            return audience ?? this._audienceService.getAudienceById(audienceId) ?? null;
        } catch {
            return this._audienceService.getAudienceById(audienceId) ?? null;
        }
    }

    ngOnInit(): void {
        // `AudienceStartComponent` is on the child route `.../audiences/:id` (path: ''),
        // so the `id` param lives on the parent route.
        (this._route.parent?.paramMap ?? this._route.paramMap)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(async (params) => {
                const audienceId = params.get('id');
                if (!audienceId) {
                    return;
                }

                this.audienceId = audienceId;
                await this._loadAudienceContext();
            });

        this._audienceService.audiences$
            .pipe(
                filter((audiences) => !!audiences),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this._syncAudienceFromStore();
            });
    }

    private _syncAudienceFromStore(): void {
        if (!this.audienceId) {
            return;
        }

        const audience = this._audienceService.getAudienceById(this.audienceId);
        if (!audience) {
            return;
        }

        this.audience = audience;
        this.isAutomaticAudienceFlag = isAutomaticAudience(audience);
        this.categories[0].lastUpdate = audience.lastUpdate ?? null;
        this.categories[1].memberCount = audience.memberCount ?? 0;
        this._cd.markForCheck();
    }

    private async _loadAudienceContext(): Promise<void> {
        const audience = await this.loadAudience(this.audienceId);
        if (!audience) {
            return;
        }

        this.audience = audience;
        this.isAutomaticAudienceFlag = isAutomaticAudience(audience);
        this.categories[0].lastUpdate = this.audience.lastUpdate ?? null;
        this.categories[1].memberCount = this.audience.memberCount ?? 0;

        let experiences = this._experienceService.getExperiences();
        if (!experiences?.length) {
            await this._experienceService.fetchExperiences();
            experiences = this._experienceService.getExperiences();
        }
        this.totalTenantExperiencesCount = experiences?.length ?? 0;

        if (!this.isAutomaticAudienceFlag) {
            const relevantExperiences = await this._audienceService.fetchAudienceRelevantExperiences(
                this.audienceId,
                AudienceExperienceStatusEnum.RELEVANT
            );
            this.categories[2].experiencesCount = relevantExperiences?.length ?? 0;
        } else {
            this.categories[2].experiencesCount = 0;
        }

        this._cd.markForCheck();
    }

    isCategoryDisabledForAutomaticAudience(category: { id: number }): boolean {
        return this.isAutomaticAudienceFlag && (category.id === 1 || category.id === 3);
    }

    renameAudience(audienceName: string): void {
        this.audience.name = audienceName;
        this._audienceService.updateAudience(this.audience);
    }

    toggleSidenav(): void {
        this._sidenavService.toggle();
    }

    openDialogEngageNewSubAudience(): void {
        this.engageNewSubAudienceDialogRef = this._matDialog.open(GrowNewSubAudienceDialogComponent, {
            disableClose: false,
            width: '600px',
        });
        this.engageNewSubAudienceDialogRef.componentInstance.audienceId = this.audience.id;
        this.engageNewSubAudienceDialogRef.componentInstance.audienceName = this.audience.name;
    }

    openDialogEngageRemoveAudience(): void {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
            width: '600px',
        });

        const dialogTexts = getRemoveAudienceDialogTexts(this.audience, (key, params) => this._translateService.instant(key, params));

        this.confirmDialogRef.componentInstance.confirmTitle = dialogTexts.confirmTitle;
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = dialogTexts.confirmMessage;
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.DELETE';

        this.confirmDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this._audienceService.deleteAudience(this.audience.id);
            }
        });
    }

    async openMoveToDialog(audience: MaxBrainAudience): Promise<void> {
        this.moveToDialogRef = this._matDialog.open(MoveToDialogComponent, {
            disableClose: false,
            width: '760px',
            autoFocus: false,
        });

        const audiences = await this._audienceService.fetchAudiencesList(undefined, EXCLUDE_AUTOMATIC_AUDIENCE_LIST_OPTIONS);
        const filteredAudiences = audiences.filter((item) => item.id !== audience.id && !item.isAutoGenerated);
        filteredAudiences.forEach((item) => (item.isSelected = false));
        const audienceListTree = this._audienceService.convertListToTree(filteredAudiences);

        this.moveToDialogRef.componentInstance.title = this._translateService.instant('GENERAL.LABEL.MOVE_TO', {
            entity: audience.name,
        });
        this.moveToDialogRef.componentInstance.listTree = audienceListTree;
        this.moveToDialogRef.componentInstance.entity = audience;

        this.moveToDialogRef.afterClosed().subscribe(async (selectedAudience: MaxBrainAudience) => {
            if (selectedAudience) {
                if (!audience.parent) {
                    audience.parent = { id: selectedAudience.id } as MaxBrainAudience['parent'];
                } else {
                    audience.parent.id = selectedAudience.id;
                }
                this._audienceService.moveToAudience(audience, selectedAudience);
            }

            this.moveToDialogRef = null;
        });
    }
}
